import { FC, ReactNode } from 'react';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

/**
 * The standard page header: an `h4` title with an optional action on the
 * trailing edge, a description underneath, and a rule closing it off.
 *
 * Extracted from the assessments home header — nothing about that layout was
 * assessment-specific, and the remediation screen had re-derived a smaller,
 * different-looking version of the same thing. Reach for this before hand-rolling
 * another page title so the app's screens keep opening the same way.
 *
 * `titleAdornment` sits inline after the title for a marker that belongs *to*
 * the title (the AI badge on remediation), as opposed to `action`, which is the
 * page's primary control and sits opposite it.
 */
export interface PageHeaderProps {
  title: ReactNode;
  /** Rendered inline directly after the title — a badge or chip, not a control. */
  titleAdornment?: ReactNode;
  description?: ReactNode;
  /** Primary page action, on the trailing edge of the title row. */
  action?: ReactNode;
  /**
   * Gap below the description, before the rule. The assessments screen sits on
   * `5` because its filter bar carries its own padding; a denser page can pass
   * less.
   */
  descriptionSpacing?: number;
  /** Set false when the caller supplies its own separation. */
  divider?: boolean;
}

export const PageHeader: FC<PageHeaderProps> = observer(
  ({
    title,
    titleAdornment,
    description,
    action,
    descriptionSpacing = 5,
    divider = true,
  }) => {
    const theme = useTheme();

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {titleAdornment}
          </Box>

          {action != null && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>

        <Box>
          {description != null && (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '1rem',
                mb: descriptionSpacing,
              }}
            >
              {description}
            </Typography>
          )}
          {divider && <Divider sx={{ borderColor: theme.palette.divider }} />}
        </Box>
      </Box>
    );
  },
);
